/**
 * Brands soft-delete / restore / permanent-delete UI helpers.
 * Keep confirmation rules aligned with kk-admin-sa-governance brand-lifecycle.
 */

export const BRAND_LIFECYCLE_MIN_REASON_LENGTH = 12;
export const BRAND_PERMANENT_DELETE_CONFIRM_TOKEN = "DELETE";

export type BrandLifecycleView = "active" | "archived";

export interface BrandEligibleMembership {
  membershipId: string;
  masterAccountId: string;
  /** Canonical Client company/business name when available. */
  companyName?: string | null;
  brandId: string;
  roleScope: string | null;
  accessLevel: string | null;
  status: string;
  revokedAt: string | null;
  revokedReason: string | null;
}

export interface BrandPurgeBlocker {
  code: string;
  message: string;
  count?: number | null;
}

export interface BrandPurgeCount {
  label: string;
  value: number | null;
}

export interface BrandPermanentDeletePreflight {
  brandId?: string | null;
  name?: string | null;
  status?: string | null;
  archived?: boolean;
  dryRun?: boolean;
  eligible: boolean;
  alreadyDeleted?: boolean;
  blockers: BrandPurgeBlocker[];
  counts: BrandPurgeCount[];
  willRemove: string[];
  willRetain: string[];
}

export interface BrandArchiveAffectedClient {
  masterAccountId: string;
  companyName: string | null;
}

/** Authoritative Archive blast-radius preflight from POST …/archive?dryRun. */
export interface BrandArchivePreflight {
  brandId?: string | null;
  name?: string | null;
  status?: string | null;
  archived?: boolean;
  dryRun?: boolean;
  platformWide?: boolean;
  eligible: boolean;
  alreadyArchived?: boolean;
  blockers: BrandPurgeBlocker[];
  checkStatus?: string;
  affectedClientCount: number;
  liveMembershipCount: number;
  locationCount: number;
  zoneConfigured?: boolean;
  zoneCount?: number | null;
  assignedToClients?: boolean;
  zeroMembershipMessage?: string | null;
  warningTitle?: string;
  warningLead?: string;
  effects: string[];
  affectedClients: BrandArchiveAffectedClient[];
  willRevokeMembershipIds?: string[];
}

export function archivePreflightFromError(
  err: unknown,
): BrandArchivePreflight | null {
  if (!err || typeof err !== "object") return null;
  const data = (err as { data?: unknown }).data;
  if (!data || typeof data !== "object") return null;
  const nested = (data as { data?: { preflight?: BrandArchivePreflight } }).data
    ?.preflight;
  if (nested && typeof nested === "object") return nested;
  const direct = (data as { preflight?: BrandArchivePreflight }).preflight;
  return direct && typeof direct === "object" ? direct : null;
}

export function formatArchiveClientLabel(
  client: BrandArchiveAffectedClient,
): string {
  const name = (client.companyName || "").trim();
  return name || `Client ${client.masterAccountId}`;
}

/** Restore checkbox label: company identity first; stable ID remains secondary. */
export function formatRestoreMembershipLabel(
  membership: BrandEligibleMembership,
): string {
  const name = (membership.companyName || "").trim();
  const identity = name || `Client ${membership.masterAccountId}`;
  const access = brandAccessLevelLabel(membership.roleScope);
  const role = membership.accessLevel ? ` · ${membership.accessLevel}` : "";
  const revoked = membership.revokedAt
    ? ` · revoked ${membership.revokedAt.slice(0, 10)}`
    : "";
  return `${identity} · ${access}${role}${revoked}`;
}

/** Default restore selection: every eligible membership checked. */
export function defaultSelectedMembershipIds(
  eligible: BrandEligibleMembership[],
): string[] {
  return eligible.map((m) => m.membershipId).filter(Boolean);
}

/**
 * Explicit membershipIds payload for restore POST.
 * Always send an array (may be empty) — never omit/null from the UI.
 */
export function membershipIdsForRestore(
  selectedIds: Iterable<string>,
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of selectedIds) {
    const trimmed = String(id || "").trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
  }
  return out;
}

export function toggleMembershipSelection(
  selectedIds: string[],
  membershipId: string,
  checked: boolean,
): string[] {
  const id = String(membershipId || "").trim();
  if (!id) return selectedIds;
  if (checked) {
    return selectedIds.includes(id) ? selectedIds : [...selectedIds, id];
  }
  return selectedIds.filter((x) => x !== id);
}

export function canSubmitBrandArchive(args: {
  brandName: string;
  nameConfirmation: string;
  reason: string;
}): boolean {
  const name = String(args.brandName || "");
  const confirmation = String(args.nameConfirmation || "");
  const reason = String(args.reason || "").trim();
  return (
    confirmation === name &&
    reason.length >= BRAND_LIFECYCLE_MIN_REASON_LENGTH
  );
}

export function canSubmitBrandPermanentDelete(args: {
  brandName: string;
  nameConfirmation: string;
  confirmTyped: string;
  reason: string;
  eligible: boolean;
}): boolean {
  if (!args.eligible) return false;
  const name = String(args.brandName || "");
  const confirmation = String(args.nameConfirmation || "");
  const reason = String(args.reason || "").trim();
  return (
    confirmation === name &&
    args.confirmTyped === BRAND_PERMANENT_DELETE_CONFIRM_TOKEN &&
    reason.length >= BRAND_LIFECYCLE_MIN_REASON_LENGTH
  );
}

export function brandAccessLevelLabel(roleScope: string | null | undefined): string {
  const scope = String(roleScope || "franchisee").toLowerCase();
  if (scope === "corporate") return "Full";
  if (scope === "franchisee") return "Limited";
  return roleScope || "Limited";
}

export function extractApiErrorMessage(error: unknown, fallback = "Request failed"): string {
  if (
    error &&
    typeof error === "object" &&
    "data" in error &&
    error.data &&
    typeof error.data === "object" &&
    "message" in error.data &&
    typeof (error as { data: { message: unknown } }).data.message === "string"
  ) {
    return (error as { data: { message: string } }).data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/** Pull preflight from a 409 permanent-delete response when present. */
export function preflightFromError(error: unknown): BrandPermanentDeletePreflight | null {
  if (!error || typeof error !== "object" || !("data" in error)) return null;
  const data = (error as { data?: { data?: { preflight?: unknown }; preflight?: unknown } })
    .data;
  const preflight = data?.data?.preflight ?? data?.preflight;
  if (!preflight || typeof preflight !== "object") return null;
  const p = preflight as BrandPermanentDeletePreflight;
  if (typeof p.eligible !== "boolean") return null;
  return p;
}
